import {z} from "zod";
import {accountIdsResponse} from "./accounts/ids";
import {accountStatusResponse} from "./accounts/status";
import {referenceDataEntityResponse} from "./referenceData";

export enum Gender {
    MALE = "male",
    FEMALE = "female",
    DIVERSE = "diverse",
}

export const gender = z.nativeEnum(Gender);

export const accountResponse = z.object({
    id: z.string().uuid(),
    ids: accountIdsResponse,
    gender: gender.optional(),
    title: z.string().optional(),
    firstname: z.string().optional(),
    lastname: z.string().optional(),
    street: z.string().optional(),
    street_no: z.string().optional(),
    supplement: z.string().optional(),
    postcode: z.string().optional(),
    city: z.string().optional(),
    country: z.string().optional(),
    email: z.string().email().optional(),
    phone: z.string().optional(),
    status: accountStatusResponse,
    profession: referenceDataEntityResponse.optional(),
    discipline: referenceDataEntityResponse.optional(),
    institution: referenceDataEntityResponse.optional(),
    subject: referenceDataEntityResponse.optional(),
    updated_at: z.date().optional(),
    created_at: z.date(),
});

export type AccountResponse = z.infer<typeof accountResponse>;

export const accountByIdRequest = z.object({
    id: z.string().uuid(),
});

export type AccountByIdRequest = z.infer<typeof accountByIdRequest>;

export const accountByEmailRequest = z.object({
    email: z.string().trim().email().toLowerCase()
})

export type AccountByEmailRequest = z.infer<typeof accountByEmailRequest>;

export enum AccountOverviewSort {
    CREATED_AT = "created_at",
    LAST_SIGN_IN_AT = "last_sign_in_at",
}

export const accountOverviewSort = z.nativeEnum(AccountOverviewSort);

export type ManagementAccountOverviewResponse = AccountResponse & {
    is_business_user: boolean;
    last_sign_in_at: string | null;
};

export const accountFetchPaginatedRequest = z.object({
    rangeStart: z.number().int().nonnegative(),
    rangeEnd: z.number().int().nonnegative(),
    search: z.string().trim().max(200).optional(),
    verified: z.boolean().optional(),
    sort: accountOverviewSort.default(AccountOverviewSort.CREATED_AT),
}).refine(
    ({rangeStart, rangeEnd}) => rangeEnd >= rangeStart && rangeEnd - rangeStart < 100,
    {message: "Account page ranges must contain between 1 and 100 records"},
);

export type AccountFetchPaginatedRequest = z.input<typeof accountFetchPaginatedRequest>;

export const accountUpdateRequest = z.object({
    id: z.string().uuid(),
    data: z.object({
        gender: gender.optional(),
        title: z.string().optional(),
        firstname: z.string().optional(),
        lastname: z.string().optional(),
        street: z.string().optional(),
        street_no: z.string().optional(),
        supplement: z.string().optional(),
        postcode: z.string().optional(),
        city: z.string().optional(),
        country: z.string().optional(),
        email: z.string().email().toLowerCase().optional(),
        phone: z.string().optional(),
        profession_id: z.string().uuid().optional(),
        discipline_id: z.string().uuid().optional(),
        institution_id: z.string().uuid().optional(),
        subject_id: z.string().uuid().optional(),
    })
});

export type AccountUpdateRequest = z.infer<typeof accountUpdateRequest>;

export const accountUpdateSignupGeneralRequest= z.object({
    id: z.string().uuid(),
    data: z.object({
        gender: gender.optional(),
        firstname: z.string().optional(),
        lastname: z.string().optional(),
        country: z.string().optional(),
        email: z.string().email().toLowerCase().optional(),
    })
});

export type AccountUpdateSignupGeneralRequest = z.infer<typeof accountUpdateSignupGeneralRequest>;

export const accountCompleteSignupGeneralRequest = z.object({
    id: z.string().uuid(),
    data: z.object({
        gender,
        firstname: z.string().trim().min(1),
        lastname: z.string().trim().min(1),
        email: z.string().email().toLowerCase(),
        country: z.string().trim().min(1),
        terms_version: z.string().trim().min(1),
        privacy_notice_version: z.string().trim().min(1),
    }),
});

export type AccountCompleteSignupGeneralRequest = z.infer<typeof accountCompleteSignupGeneralRequest>;

export const accountUpdatePersonalDetailsRequest = z.object({
    id: z.string().uuid(),
    data: z.object({
        gender: gender.optional(),
        title: z.string().optional(),
        firstname: z.string().optional(),
        lastname: z.string().optional(),
        email: z.string().email().toLowerCase().optional(),
        phone: z.string().optional(),
    })
});

export type AccountUpdatePersonalDetailsRequest = z.infer<typeof accountUpdatePersonalDetailsRequest>;

export const accountUpdateBusinessDetailsRequest = z.object({
    id: z.string().uuid(),
    data: z.object({
        street: z.string().optional(),
        street_no: z.string().optional(),
        supplement: z.string().optional(),
        postcode: z.string().optional(),
        city: z.string().optional(),
        country: z.string().optional(),
    })
});

export type AccountUpdateBusinessDetailsRequest = z.infer<typeof accountUpdateBusinessDetailsRequest>;

export const accountUpdateProfessionRequest = z.object({
    id: z.string().uuid(),
    data: z.object({
        profession_id: z.string().uuid(),
    })
});

export type AccountUpdateProfessionRequest = z.infer<typeof accountUpdateProfessionRequest>;

export const accountUpdateDisciplineRequest = z.object({
    id: z.string().uuid(),
    data: z.object({
        discipline_id: z.string().uuid(),
    })
});

export type AccountUpdateDisciplineRequest = z.infer<typeof accountUpdateDisciplineRequest>;

export const accountUpdateInstitutionRequest = z.object({
    id: z.string().uuid(),
    data: z.object({
        institution_id: z.string().uuid(),
    })
});

export type AccountUpdateInstitutionRequest = z.infer<typeof accountUpdateInstitutionRequest>;

export const accountUpdateSubjectRequest = z.object({
    id: z.string().uuid(),
    data: z.object({
        subject_id: z.string().uuid(),
    })
});

export type AccountUpdateSubjectRequest = z.infer<typeof accountUpdateSubjectRequest>;

export const accountDeleteRequest = z.object({
    id: z.string().uuid(),
    provider_apple_authorization_code: z.string().optional(),
});

export type AccountDeleteRequest = z.infer<typeof accountDeleteRequest>;
